import { useEffect, useRef, useState } from "react";
import s from "./teacher.module.scss";
import { useParams, useSearchParams, NavLink, useNavigate } from "react-router-dom";
import { IconArrowLeft, IconPackage, IconArrowRight, IconBeach } from "@tabler/icons-react";
import { useAvaiableTeacherPacks } from "@widgets/student/ui/teacher-profile/api/useAvaiableTeacherPacks";
import { useTeacher } from "@entities/teacher/api/useTeacher";
import { FetchingDiv } from "@shared/ui/fetching-div/fetching-div";
import { ChooseDate } from "@widgets/student/ui/choose-date/choose-date";
import { PayCard } from "@widgets/student/ui/pay-card/pay-card";
import { TeacherInfo } from "@widgets/student/ui/teacher-info/teacher-info";
import { useAllOrders } from "@entities/schedule/api/useAllOrders";
import { useActivePacks } from "@shared/api/useActivePacks";
import { useEnabledPromocode } from "@widgets/student/api/useEnabledPromocode";
import { UnpaidOrderAlert } from "@widgets/student/ui/unpaid-order-alert/unpaid-order-alert";
import useMedia from "use-media";
import { Button } from "@shared/ui/button/button";
import { IconX } from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { isTrialAvailable } from "@shared/lib/trial";

export const Teacher = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedDate, set_selectedDate] = useState(() => searchParams.get("date") || null);
  const [selectedTime, set_selectedTime] = useState(() => searchParams.get("time") || null);
  const [selectedInstrument, set_selectedInstrument] = useState(null);
  const [isCheckoutOpen, set_isCheckoutOpen] = useState(false);

  const isMobile = useMedia({ maxWidth: "768px" });
  const { data: teacher, isFetched } = useTeacher(id);
  const { data: packs } = useAvaiableTeacherPacks(id);
  const { data: orders } = useAllOrders();
  const { data: activePacks } = useActivePacks();
  const { data: checkoutPrice } = useEnabledPromocode({
    promocode: null,
    teacher_id: teacher?.id,
  });

  const checkoutBasePrice = Number(checkoutPrice?.price);
  const checkoutFinalPrice = Number(checkoutPrice?.discounted_price);
  const checkoutHasPrice =
    !Number.isNaN(checkoutBasePrice) && !Number.isNaN(checkoutFinalPrice);
  const checkoutHasDiscount =
    checkoutHasPrice && checkoutFinalPrice !== checkoutBasePrice;
  const checkoutWithoutPay =
    checkoutPrice?.outside_payment_available || checkoutFinalPrice === 0;
  const isTrial = isTrialAvailable(teacher);
  const vacationEnabled = !!teacher?.vacation_enabled;
  const hasActivePackWithTeacher = !!activePacks?.data?.find(
    (pack) =>
      String(pack?.teacher?.teacher_slug) === String(id) &&
      (pack?.lessons_used ?? 0) < (pack?.lessons_count ?? 0)
  );
  // В отпуске новая запись запрещена, но запись по активному пакету разрешена
  const vacationBlocked = vacationEnabled && !hasActivePackWithTeacher;

  const initialInstrumentIdRef = useRef(searchParams.get("instrument"));

  useEffect(() => {
    if (!teacher?.instruments?.length || selectedInstrument) return;

    const instrumentId = initialInstrumentIdRef.current;
    set_selectedInstrument(
      teacher.instruments.find((i) => String(i.id) === instrumentId) ||
        teacher.instruments[0]
    );
  }, [teacher, selectedInstrument]);

  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    let changed = false;

    const setParam = (key, value) => {
      if (value) {
        if (next.get(key) !== String(value)) {
          next.set(key, value);
          changed = true;
        }
      } else if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    };

    setParam("date", selectedDate);
    setParam("time", selectedTime);
    setParam("instrument", selectedInstrument?.id);

    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [selectedDate, selectedTime, selectedInstrument, setSearchParams]);

  useEffect(() => {
    if (localStorage.getItem("teacher_id")) {
      localStorage.removeItem("teacher_id");
    }
  }, []);

  return (
    <>
      <FetchingDiv className={s.wrapper} isFetching={!isFetched}>
        <button
          type="button"
          className={s.backButton}
          onClick={() => navigate(-1)}
        >
          <IconArrowLeft size={18} />
          Назад
        </button>
        <div className={s.title}>Забронировать занятие</div>
        {orders?.length > 0 && (
          <div className={s.alertWrapper}>
            <UnpaidOrderAlert orderId={orders?.[0].id} />
          </div>
        )}
        <div className={s.flex}>
          <div className={s.card}>
            <TeacherInfo />
            {packs?.length > 0 && !isTrial && !vacationEnabled ? (
              <NavLink to={`/teacher/packs/${id}`} className={s.packBanner}>
                <span className={s.packBannerContent}>
                  <span className={s.packBannerIcon}>
                    <IconPackage size={20} />
                  </span>
                  <span className={s.packBannerText}>
                    <span className={s.packBannerTitle}>Купить пакет занятий</span>
                    <span className={s.packBannerSubtitle}>Выгоднее, чем по одному</span>
                  </span>
                </span>
                <IconArrowRight size={18} />
              </NavLink>
            ) : null}
            {vacationBlocked ? (
              <div className={s.vacationNotice}>
                <IconBeach size={24} className={s.vacationNoticeIcon} />
                <div className={s.vacationNoticeTitle}>
                  Преподаватель временно не принимает запись
                </div>
                <div className={s.vacationNoticeText}>
                  Преподаватель находится в отпуске. Новая запись и покупка пакетов временно недоступны.
                </div>
              </div>
            ) : (
            <ChooseDate
              teacher_id={id}
              {...{
                selectedDate,
                set_selectedDate,
                selectedTime,
                set_selectedTime,
                selectedInstrument,
                set_selectedInstrument,
              }}
            />
            )}
          </div>
          {!isMobile && !vacationBlocked && (
            <div className={s.rightColumn}>
              {selectedDate && selectedInstrument && selectedTime && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                  <PayCard
                    {...{
                      selectedTime,
                      selectedDate,
                      selectedInstrument,
                      set_selectedTime,
                    }}
                  />
                </motion.div>
              )}
            </div>
          )}
        </div>
      </FetchingDiv>

      {isMobile && !vacationBlocked && (
        <div className={s.mobileCheckoutBar}>
          <Button
            onClick={() => set_isCheckoutOpen(true)}
            className={s.mobileCheckoutBtn}
            disabled={!selectedDate || !selectedInstrument || !selectedTime}
          >
            <span className={s.mobileCheckoutBtnContent}>
              {isTrial ? "Записаться" : checkoutWithoutPay ? "Забронировать занятие" : "Перейти к оплате"}
              {!checkoutWithoutPay && !isTrial && checkoutHasPrice && (
                <span className={s.mobileCheckoutPrice}>
                  {checkoutHasDiscount && (
                    <span className={s.mobileCheckoutOldPrice}>
                      {checkoutBasePrice.toLocaleString("ru-RU")} ₽
                    </span>
                  )}
                  {checkoutFinalPrice.toLocaleString("ru-RU") + " ₽"}
                </span>
              )}
            </span>
          </Button>
        </div>
      )}

      <AnimatePresence>
        {isMobile && isCheckoutOpen && (
          <motion.div
            className={s.checkoutSheetOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <motion.div
              className={s.checkoutSheetBackdrop}
              onClick={() => set_isCheckoutOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            />
            <motion.div
              className={s.checkoutSheet}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className={s.checkoutSheetHeader}>
                <span className={s.checkoutSheetTitle}>Оплата занятия</span>
                <button
                  type="button"
                  className={s.checkoutSheetClose}
                  onClick={() => set_isCheckoutOpen(false)}
                  aria-label="Закрыть"
                >
                  <IconX size={24} />
                </button>
              </div>
              <div className={s.checkoutSheetBody}>
                <PayCard
                  {...{
                    selectedTime,
                    selectedDate,
                    selectedInstrument,
                    set_selectedTime,
                  }}
                  submitLabel="Оплатить"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
